import { useTheme } from "@emotion/react";
import { Email, MarkEmailRead, Refresh } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  Typography
} from "@mui/material";
import { reload, sendEmailVerification } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../Themes";
import AuthWrapper from "./AuthWrapper";

const VerificationRequiredPage = ({ userEmail: propUserEmail }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { signOut } = cloudProvider;
  const location = useLocation();
  const navigate = useNavigate();

  // Get user email from props, navigation state, or current user
  const userEmail = propUserEmail ||
    location.state?.userEmail ||
    cloudProvider?.auth?.currentUser?.email;

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastResent, setLastResent] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [initialEmailSent, setInitialEmailSent] = useState(false);

  // Get theme for gradient colors
  const theme = useTheme();
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  const checkVerificationStatus = useCallback(async () => {
    if (!cloudProvider?.auth?.currentUser) return;

    setCheckingStatus(true);
    try {
      // Reload user to get fresh verification status
      await reload(cloudProvider.auth.currentUser);

      if (cloudProvider.auth.currentUser.emailVerified) {
        setIsVerified(true);
        // Redirect or reload on verification
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    } finally {
      setCheckingStatus(false);
    }
  }, [cloudProvider?.auth?.currentUser]);

  // Send initial verification email on component mount
  useEffect(() => {
    const sendInitialVerificationEmail = async () => {
      if (cloudProvider?.auth?.currentUser && !cloudProvider.auth.currentUser.emailVerified && !initialEmailSent) {
        try {
          setInitialEmailSent(true);
          setLastResent(new Date());
          await sendEmailVerification(cloudProvider.auth.currentUser, {
            url: window.location.origin + '/',
            handleCodeInApp: false
          });
          console.log('Initial verification email sent successfully');
        } catch (error) {
          setLastResent(new Date(Date.now() - 60000));
          console.error('Error sending initial verification email:', error);
        }
      }
    };

    if (cloudProvider?.auth?.currentUser) {
      if (!cloudProvider.auth.currentUser.emailVerified && !initialEmailSent) {
        // Send verification email on first load
        sendInitialVerificationEmail();
      }
      checkVerificationStatus();

      // Check every 5 seconds for verification status
      const interval = setInterval(() => {
        checkVerificationStatus();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [cloudProvider?.auth?.currentUser, initialEmailSent, checkVerificationStatus]);

  const handleResendVerification = async () => {
    if (!cloudProvider?.auth?.currentUser) {
      setError(true);
      setErrorMessage('No user is currently signed in');
      return;
    }

    setLoading(true);
    setError(false);
    setSuccess(false);
    setErrorMessage('');

    try {
      // Use Firebase Auth's built-in sendEmailVerification
      await sendEmailVerification(cloudProvider.auth.currentUser, {
        url: window.location.origin + '/',
        handleCodeInApp: false
      });

      setSuccess(true);
      setLastResent(new Date());
    } catch (error) {
      console.error('Error resending verification:', error);
      setError(true);

      // Handle specific Firebase Auth errors
      switch (error.code) {
        case 'auth/too-many-requests':
          setErrorMessage('Too many requests. Please try again later.');
          break;
        case 'auth/user-not-found':
          setErrorMessage('User not found. Please sign in again.');
          break;
        case 'auth/network-request-failed':
          setErrorMessage('Network error. Please check your connection.');
          break;
        default:
          setErrorMessage(error.message || 'An error occurred while resending verification email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    try {
      await signOut();
      navigate('/login', { preventScrollReset: false });
    } catch (error) {
      console.error('Error signing out:', error);
      // Navigate anyway even if signOut fails
      navigate('/login', { preventScrollReset: false });
    }
  };

  const handleTryDifferentEmail = async () => {
    try {
      await signOut();
      navigate('/login', { preventScrollReset: false });
    } catch (error) {
      console.error('Error signing out:', error);
      // Navigate anyway even if signOut fails
      navigate('/login', { preventScrollReset: false });
    }
  };

  const canResend = () => {
    if (!lastResent) return true;
    const timeSinceLastResend = Date.now() - lastResent.getTime();
    return timeSinceLastResend > 60000; // 1 minute cooldown
  };

  const getResendButtonText = () => {
    if (loading) {
      return localizationProvider.translateByType({
        type: "Internal",
        text: "sending"
      }) || "Sending...";
    }

    if (!canResend()) {
      const timeLeft = Math.ceil((60000 - (Date.now() - lastResent.getTime())) / 1000);
      return `Resend in ${timeLeft}s`;
    }

    return localizationProvider.translateByType({
      type: "Internal",
      text: "resendVerification"
    }) || "Resend Verification Email";
  };

  // Show success screen when verified
  if (isVerified) {
    return (
      <AuthWrapper autoRedirect={false}>
        <Avatar
          sx={{
            m: '0 auto',
            mb: 2,
            width: 56,
            height: 56,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            boxShadow: `0 8px 32px ${theme.palette.success.main}30`
          }}
        >
          <MarkEmailRead sx={{ fontSize: 28 }} />
        </Avatar>
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "emailVerified"
          }) || "Email Verified!"}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {localizationProvider.translateByType({
            type: "Internal",
            text: "emailVerifiedMessage"
          }) || "Your email has been successfully verified. Redirecting..."}
        </Typography>
        <CircularProgress sx={{ mt: 2 }} size={24} />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper autoRedirect={false}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            m: '0 auto',
            mb: 2,
            width: 56,
            height: 56,
            background: primaryGradient,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`
          }}
        >
          <Email sx={{ fontSize: 28 }} />
        </Avatar>
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 700,
            background: primaryGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "verifyEmail"
          }) || "Verify Your Email"}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "verificationEmailSent"
          }) || "We've sent a verification email to"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            mt: 1,
            mb: 2
          }}
        >
          {userEmail}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.95rem' }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "checkEmailInstructions"
          }) || "Please check your email and click the verification link to continue."}
        </Typography>
      </Box>

      {/* Status checking indicator */}
      {checkingStatus && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {localizationProvider.translateByType({
              type: "Internal",
              text: "checkingVerificationStatus"
            }) || "Checking verification status..."}
          </Typography>
        </Box>
      )}

      {/* Success message */}
      {success && (
        <Fade in={success}>
          <Alert
            severity='success'
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(76, 175, 80, 0.2)'
            }}
          >
            {localizationProvider.translateByType({
              type: "Internal",
              text: "verificationEmailResent"
            }) || "Verification email has been resent successfully!"}
          </Alert>
        </Fade>
      )}

      {/* Error message */}
      {error && (
        <Fade in={error}>
          <Alert
            severity='error'
            sx={{
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(211, 47, 47, 0.2)'
            }}
          >
            {errorMessage || localizationProvider.translateByType({
              type: "Internal",
              text: "verificationEmailError"
            }) || "Failed to resend verification email. Please try again."}
          </Alert>
        </Fade>
      )}

      {/* Action buttons */}
      <Box sx={{ mt: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleResendVerification}
          disabled={loading || !canResend()}
          sx={{
            mb: 2,
            py: 1.5,
            borderRadius: 3,
            background: primaryGradient,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              transform: 'none',
              boxShadow: 'none'
            }
          }}
          startIcon={loading ? <CircularProgress sx={{
            mx: 1
          }} size={20} color="inherit" /> : <Refresh sx={{
            mx: 1
          }} />}
        >
          {getResendButtonText()}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={checkVerificationStatus}
          disabled={checkingStatus}
          sx={{
            py: 1.5,
            borderRadius: 3,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              background: `${theme.palette.primary.main}08`,
              transform: 'translateY(-1px)'
            }
          }}
          startIcon={checkingStatus ? <CircularProgress sx={{
            mx: 1
          }} size={20} color="inherit" /> : <MarkEmailRead sx={{
            mx: 1
          }} />}
        >
          {checkingStatus
            ? (localizationProvider.translateByType({
              type: "Internal",
              text: "checking"
            }) || "Checking...")
            : (localizationProvider.translateByType({
              type: "Internal",
              text: "checkVerificationStatus"
            }) || "I've Verified My Email")
          }
        </Button>
      </Box>

      {/* Help text */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {localizationProvider.translateByType({
            type: "Internal",
            text: "didntReceiveEmail"
          }) || "Didn't receive the email?"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {localizationProvider.translateByType({
            type: "Internal",
            text: "checkSpamFolder"
          }) || "Check your spam folder or click resend above."}
        </Typography>
      </Box>

      {/* Navigation buttons */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="text"
            onClick={handleBackToLogin}
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 500,
              fontSize: '0.9rem',
              textTransform: 'none',
              transition: 'all 0.3s ease',
              borderRadius: 2,
              '&:hover': {
                color: theme.palette.primary.dark,
                background: `${theme.palette.primary.main}08`
              }
            }}
          >
            {localizationProvider.translateByType({
              type: "Internal",
              text: "backToLogin"
            }) || "← Back to Login"}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            fullWidth
            variant="text"
            onClick={handleTryDifferentEmail}
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 500,
              fontSize: '0.9rem',
              textTransform: 'none',
              transition: 'all 0.3s ease',
              borderRadius: 2,
              '&:hover': {
                color: theme.palette.primary.dark,
                background: `${theme.palette.primary.main}08`
              }
            }}
          >
            {localizationProvider.translateByType({
              type: "Internal",
              text: "tryDifferentEmail"
            }) || "Try Different Email →"}
          </Button>
        </Grid>
      </Grid>
    </AuthWrapper>
  );
};

export default VerificationRequiredPage;
